import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2, Edit, Shield } from 'lucide-react';
import { useTheme } from '../../ThemeContext';
import { Role } from '../../types';
import { getRoles, deleteRole } from '../../utils/auth/authService';

const RolesPage: React.FC = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const rolesData = await getRoles();
      setRoles(rolesData);
    } catch (err) {
      setError('Failed to load roles');
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (roleId: string, isSystemRole: boolean) => {
    if (isSystemRole) {
      alert('Cannot delete system roles');
      return;
    }
    if (!window.confirm('Are you sure you want to delete this role?')) return;
    try {
      await deleteRole(roleId);
      await loadData();
    } catch (err) {
      alert('Failed to delete role');
    }
  };

  const getPermissionCount = (permissionIds: string[]) => {
    return permissionIds.length;
  };

  if (loading) {
    return <div style={{ color: theme.text }}>Loading...</div>;
  }

  return (
    <div style={{ padding: 24 }}>
      {error && (
        <div style={{
          background: '#fee',
          color: '#c33',
          padding: 12,
          borderRadius: 4,
          marginBottom: 16,
          border: '1px solid #fcc',
        }}>
          {error}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ color: theme.text, fontSize: 24, margin: 0 }}>Roles</h1>
        <button
          onClick={() => navigate('/auth/roles/new')}
          style={{
            padding: '10px 20px',
            background: theme.buttonBackground,
            color: theme.buttonText,
            border: 'none',
            borderRadius: 4,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            fontWeight: 500,
          }}
        >
          <Plus size={18} />
          Add Role
        </button>
      </div>

      {roles.length === 0 ? (
        <div style={{
          background: theme.cardBackground,
          border: `1px solid ${theme.border}`,
          borderRadius: 8,
          padding: 48,
          textAlign: 'center',
        }}>
          <p style={{ color: theme.textSecondary, fontSize: 16, margin: 0 }}>
            No roles found
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {roles.map((role) => (
            <div
              key={role.role_id}
              onClick={() => navigate(`/auth/roles/${role.role_id}`)}
              style={{
                background: theme.cardBackground,
                border: `1px solid ${theme.border}`,
                borderRadius: 8,
                padding: 20,
                cursor: 'pointer',
                transition: 'border-color 0.2s',
              }}
              onMouseEnter={(e) => e.currentTarget.style.borderColor = theme.accent}
              onMouseLeave={(e) => e.currentTarget.style.borderColor = theme.border}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                    <Shield size={20} color={theme.accent} />
                    <h3 style={{ color: theme.text, fontSize: 18, margin: 0, textTransform: 'uppercase' }}>
                      {role.name}
                    </h3>
                    {role.is_system_role && (
                      <span style={{
                        padding: '4px 8px',
                        background: '#e0f2fe',
                        color: '#0369a1',
                        borderRadius: 4,
                        fontSize: 12,
                        fontWeight: 600,
                      }}>
                        System Role
                      </span>
                    )}
                  </div>
                  
                  <p style={{ color: theme.textSecondary, fontSize: 14, marginBottom: 12 }}>
                    {role.description || 'No description provided'}
                  </p>
                  
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ color: theme.textSecondary, fontSize: 13 }}>
                      <strong>{getPermissionCount(role.permissions)}</strong> permissions
                    </span>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 8 }} onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={() => navigate(`/auth/roles/${role.role_id}`)}
                    style={{
                      padding: 8,
                      background: 'transparent',
                      border: `1px solid ${theme.border}`,
                      borderRadius: 4,
                      cursor: role.is_system_role ? 'not-allowed' : 'pointer',
                      color: theme.text,
                      display: 'flex',
                      alignItems: 'center',
                      opacity: role.is_system_role ? 0.5 : 1,
                    }}
                    title={role.is_system_role ? 'Cannot edit system roles' : 'View/Edit'}
                    disabled={role.is_system_role}
                  >
                    <Edit size={16} />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(role.role_id, role.is_system_role);
                    }}
                    style={{
                      padding: 8,
                      background: 'transparent',
                      border: `1px solid ${theme.border}`,
                      borderRadius: 4,
                      cursor: role.is_system_role ? 'not-allowed' : 'pointer',
                      color: role.is_system_role ? theme.textSecondary : '#ef4444',
                      display: 'flex',
                      alignItems: 'center',
                      opacity: role.is_system_role ? 0.5 : 1,
                    }}
                    title={role.is_system_role ? 'Cannot delete system roles' : 'Delete'}
                    disabled={role.is_system_role}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default RolesPage;
